import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

// Lazy load non-critical routes for faster initial page load
const Careers = lazy(() => import("./pages/Careers"));
const About = lazy(() => import("./pages/About"));
const Help = lazy(() => import("./pages/Help"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Webinars = lazy(() => import("./pages/Webinars"));
const Editorial = lazy(() => import("./pages/Editorial"));
const ArticleFeatured = lazy(() => import("./pages/ArticleFeatured"));
const ArticleCampaignMonitoring = lazy(() => import("./pages/ArticleCampaignMonitoring"));
const ArticleDataSilos = lazy(() => import("./pages/ArticleDataSilos"));
const ArticleMarketingAutomation = lazy(() => import("./pages/ArticleMarketingAutomation"));
const ArticleManualWork = lazy(() => import("./pages/ArticleManualWork"));
const ArticleAICreative = lazy(() => import("./pages/ArticleAICreative"));
const ArticleOperationsEfficiency = lazy(() => import("./pages/ArticleOperationsEfficiency"));
const ArticleScalingWithoutHeadcount = lazy(() => import("./pages/ArticleScalingWithoutHeadcount"));
const ArticleUnitEconomics = lazy(() => import("./pages/ArticleUnitEconomics"));
const ArticleTemplate = lazy(() => import("./pages/ArticleTemplate"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Products = lazy(() => import("./pages/Products"));
const Agencies = lazy(() => import("./pages/Agencies"));
const SmallBusinesses = lazy(() => import("./pages/SmallBusinesses"));
const DTCBrands = lazy(() => import("./pages/DTCBrands"));
const GenerateResults = lazy(() => import("./pages/GenerateResults"));
const Audit = lazy(() => import("./pages/Audit"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/about" element={<About />} />
              <Route path="/help" element={<Help />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/webinars" element={<Webinars />} />
              <Route path="/editorial" element={<Editorial />} />
              <Route path="/editorial/ai-ecommerce-future" element={<ArticleFeatured />} />
              <Route path="/editorial/ai-strategies-dtc-brand" element={<ArticleCampaignMonitoring />} />
              <Route path="/editorial/machine-learning-recommendations" element={<ArticleDataSilos />} />
              <Route path="/editorial/supply-chain-predictive-analytics" element={<ArticleMarketingAutomation />} />
              <Route path="/editorial/ai-generated-creative-best-practices" element={<ArticleManualWork />} />
              <Route path="/editorial/small-brand-300-growth" element={<ArticleAICreative />} />
              <Route path="/editorial/operations-efficiency" element={<ArticleOperationsEfficiency />} />
              <Route path="/editorial/scaling-without-headcount" element={<ArticleScalingWithoutHeadcount />} />
              <Route path="/editorial/unit-economics-automation" element={<ArticleUnitEconomics />} />
              <Route path="/editorial/:slug" element={<ArticleTemplate />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/product" element={<Products />} />
              <Route path="/generate" element={<GenerateResults />} />
              <Route path="/solutions/agencies" element={<Agencies />} />
              <Route path="/solutions/small-businesses" element={<SmallBusinesses />} />
              <Route path="/solutions/dtc-brands" element={<DTCBrands />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/privacy" element={<Privacy />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
